(ns app.front.view.group.admin.users.service-accounts
  (:require [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.view.group.admin.add-user :as add-user]
            [app.front.view.group.admin.remove-user :as remove-user]
            [app.front.view.profile.api-keys :as profile-api-keys]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::add-account
  (fn [{:keys [db]} _]
    {:comm/command {:command    :group/add-service-account
                    :data       (-> db
                                    ::add-user/document
                                    (select-keys [:user/username
                                                  :user/groups
                                                  ::add-user/parent-group-id])
                                    (update :user/groups #(map :db/id %))
                                    (set/rename-keys {::add-user/parent-group-id :parent-group-id}))
                    :notify     {:success :group-admin.service-accounts/add-success}
                    :on-success [::add-account.success]}}))

(rf/reg-event-fx ::add-account.success
  ;; `::after-add` is the opener's event, the added user appended; by default
  ;; the legacy user management's list takes the new account.
  (fn [{:keys [db]} [_ _ added-user]]
    {:db       (-> (assoc db ::modal-open? false)
                   (dissoc ::after-add)
                   add-user/reset-db)
     :dispatch (conj (or (::after-add db)
                         [:app.front.view.group.admin.user-management/add-new-user-to-list])
                     added-user)}))

(rf/reg-event-fx ::add-api-key
  (fn [_ [_ group-id user]]
    {:comm/command {:command    :group/create-service-account-api-key
                    :data       (util/mmap {:user-id (:db/id user)} group-id)
                    :notify     {:success :api-key-ui/add-new-success}
                    :on-success [::add-api-key.success user]}}))

(rf/reg-event-fx ::add-api-key.success
  (fn [_ [_ user _ new-key]]
    {:dispatch [:app.front.view.group.admin.user-management/add-new-user-to-list
                (update user :user/api-keys concat [new-key])]}))

(rf/reg-event-fx ::update-api-key.success
  (fn [_ [_ user _ {:db/keys [id] :as new-data}]]
    {:dispatch [:app.front.view.group.admin.user-management/add-new-user-to-list
                (update user
                        :user/api-keys
                        (fn [api-keys]
                          (p/update-first api-keys {:db/id id} merge new-data)))]}))

(rf/reg-event-fx ::delete-api-key.success
  (fn [_ [_ user id]]
    {:dispatch [:app.front.view.group.admin.user-management/add-new-user-to-list
                (update user
                        :user/api-keys
                        (fn [api-keys]
                          (remove #(-> % :db/id (= id)) api-keys)))]}))

(rf/reg-event-db ::open-modal
  (fn [db [_ group-id user-groups after-add]]
    (-> (assoc db ::modal-open? true)
        (assoc ::after-add after-add)
        (assoc ::add-user/document {::add-user/user-groups     user-groups
                                    ::add-user/parent-group-id group-id}))))

(rf/reg-event-db ::close-clicked
  (fn [db _]
    (assoc db ::modal-open? false)))

(rf/reg-sub ::modal-open? :-> ::modal-open?)

(defn add-account-modal [{:keys [tr group]}]
  (let [open?    (<sub [::modal-open?])
        username (<sub [::add-user/document [:user/username]])
        groups   (<sub [::add-user/document [:user/groups]])
        invalid? (boolean (or (str/blank? username)
                              (empty? groups)))]
    (cc/modal
      {:open                    (boolean open?)
       :primary-button-text     (tr [:add])
       :secondary-button-text   (tr [:cancel])
       :modal-heading           (tr [:group-admin.service-accounts/add-new])
       :on-request-close        #(>evt [::close-clicked])
       :on-request-submit       #(>evt [::add-account])
       :on-secondary-submit     #(>evt [::close-clicked])
       :selector-primary-focus  "#service-account"
       :primary-button-disabled invalid?}
      (when open?
        [:div.flex.flex-col
         [:p (tr [:group-admin.service-accounts/username-guide])]
         [:form
          [:div.flex.items-center.mb-3
           [ui/text-input {:id            "service-account"
                           :type          :text
                           :label-text    (tr [:group-admin.service-accounts/username-label])
                           :auto-complete :off
                           :light         false
                           :auto-focus    true
                           :required      true
                           :value         username
                           :on-change     (add-user/val->evt [:user/username])}]
           [:span.ml-2.mt-6.bold (->> group group-model/group-domain (str "@"))]]

          [:fieldset
           [:div.bx--row
            [:div.bx--col-xs-12
             (cc/form-group
               {:legend-text ""}
               [add-user/user-groups-checkboxes tr {:service-account? true}])]]]]]))))

(defn expanded-content [{:keys [group-editor tr user-groups user group]}]
  [:div.lg:px-6.py-2
   [:div.border.border-ui-gray.border-solid.rounded.p-4
    [:h4.mb-2 (tr [:group-admin.service-accounts/permissions-heading])]
    [group-editor (util/mmap tr user-groups user)]]
   [:div.border.border-ui-gray.border-solid.rounded.p-4.mt-2
    [:h4.mb-2 (tr [:api-key-ui/section-title])]
    (if-let [api-keys (seq (:user/api-keys user))]
      [profile-api-keys/api-key-table (util/mmap {:update-success-evt [::update-api-key.success user]
                                                  :delete-success-evt [::delete-api-key.success user]}
                                                 tr api-keys)]
      [:div (tr [:group-admin.service-accounts/no-api-keys])])
    [:div.mt-6.flex.w-full.justify-end
     (cc/button {:icon     i/plus
                 :on-click #(>evt [::add-api-key (:db/id group) user])
                 :kind     :tertiary
                 :size     :small}
                (tr [:api-key-ui/add-new]))]]
   [:div.flex.justify-end.mt-4
    [remove-user/remove-button (util/mmap {:group-id (:db/id group)
                                           :tr-key   :group-admin.service-accounts/remove-account}
                                          tr user)]]])

(defn section [{:keys [tr group user-groups service-accounts group-editor]}]
  [section/section
   "group-service-accounts"
   {:tr             tr
    :heading-button (cc/button {:on-click #(>evt [::open-modal (:db/id group) user-groups])
                                :icon     i/plus
                                :kind     :primary
                                :size     :small}
                               (tr [:group-admin.service-accounts/add-new]))}
   :group-admin.service-accounts/section-heading
   [:p.lg:w-2-3.xxl:w-1-2.pb-6
    (tr [:group-admin.service-accounts/section-guide])]
   [ui-table/modern-responsive-table
    {:items         service-accounts
     :tr            tr
     :cols          [[:user/username
                      :user/username]
                     [:user-management/groups
                      (fn [{:user/keys [groups]}]
                        (let [gids (-> user-groups du/entity-id-set)]
                          (->> groups
                               (filter (comp gids :db/id))
                               (map :group/name)
                               sort
                               (str/join ", "))))]]
     :expanded-func (fn [user]
                      [expanded-content (util/mmap tr user-groups user group-editor group)])}]
   [add-account-modal (util/mmap tr group)]])
